import authApi from "../api/authApi";
import appConfig from "../config/appConfig";

const authService = {
  /**
   * Log in a user.
   * @param {Object} credentials - { username, password }
   * @returns {Promise<Object>} Resolves with user data on success.
   */
  login: async (credentials) => {
    try {
      const { access_token, refresh_token } = await authApi.login(credentials);
      
      if (!access_token) {
        throw new Error("No access token received from backend");
      }
  
      // Store token in localStorage
      localStorage.setItem(appConfig.auth.tokenStorageKey, access_token);
      if (refresh_token) {
        localStorage.setItem(appConfig.auth.refreshTokenStorageKey, refresh_token);
      }
  
      // Fetch user profile after login
      const user = await authApi.getProfile();
      if (!user) {
        throw new Error("Failed to fetch user profile.");
      }
  
      localStorage.setItem(appConfig.auth.userStorageKey, JSON.stringify(user));
  
      return user;
    } catch (error) {
      console.error("Login error:", error);
      throw new Error(error.response?.data?.detail || "Login failed");
    }
  },
  
  /**
   * Register a new user.
   * @param {Object} userData - { username, email, password }
   * @returns {Promise<Object>} Registered user data.
   */
  register: async (userData) => {
    try {
      await authApi.register(userData);
      return authService.login({
        username: userData.username,
        password: userData.password,
      });
    } catch (error) {
      console.error("Registration error:", error);
      throw new Error(error.response?.data?.detail || "Registration failed");
    }
  },

  /**
   * Log out a user (clears stored token and user data).
   */
  logout: async () => {
    const refreshToken = localStorage.getItem(appConfig.auth.refreshTokenStorageKey);
    localStorage.removeItem(appConfig.auth.tokenStorageKey);
    localStorage.removeItem(appConfig.auth.refreshTokenStorageKey);
    localStorage.removeItem(appConfig.auth.userStorageKey);
    if (refreshToken) {
      await authApi.logout(refreshToken).catch(() => undefined);
    }
  },

  googleLogin: async (idToken) => {
    const { access_token, refresh_token } = await authApi.googleLogin(idToken);
    if (!access_token) throw new Error('No access token received from backend');
    localStorage.setItem(appConfig.auth.tokenStorageKey, access_token);
    if (refresh_token) localStorage.setItem(appConfig.auth.refreshTokenStorageKey, refresh_token);
    const user = await authApi.getProfile();
    localStorage.setItem(appConfig.auth.userStorageKey, JSON.stringify(user));
    return user;
  },

  refreshSession: async () => {
    const refreshToken = localStorage.getItem(appConfig.auth.refreshTokenStorageKey);
    if (!refreshToken) throw new Error('No refresh session is available.');
    const session = await authApi.refresh(refreshToken);
    localStorage.setItem(appConfig.auth.tokenStorageKey, session.access_token);
    localStorage.setItem(appConfig.auth.refreshTokenStorageKey, session.refresh_token);
    return session;
  },

  logoutAll: async () => {
    await authApi.logoutAll();
    localStorage.removeItem(appConfig.auth.tokenStorageKey);
    localStorage.removeItem(appConfig.auth.refreshTokenStorageKey);
    localStorage.removeItem(appConfig.auth.userStorageKey);
  },

  /**
   * Retrieve the current user from storage.
   * @returns {Object|null} The current user data or null if not authenticated.
   */
  getCurrentUser: () => {
    try {
      let userData =
        localStorage.getItem(appConfig.auth.userStorageKey) ||
        sessionStorage.getItem(appConfig.auth.userStorageKey);

      if (!userData || userData === "undefined") {
        return null;
      }
      const user = JSON.parse(userData);
      // console.log("Retrieved user from storage:", user);
      return user;
    } catch (error) {
      console.error("Error parsing user data:", error);
      return null;
    }
  },

  /**
   * Check if a user is authenticated.
   * @returns {boolean} True if a token exists, false otherwise.
   */
  isAuthenticated: () => {
    const token = localStorage.getItem(appConfig.auth.tokenStorageKey);
    // console.log("Token from storage:", token);
    return !!token;
  },

  /**
   * Get the current authentication token.
   * @returns {string|null} The stored token or null.
   */
  getToken: () => {
    return localStorage.getItem(appConfig.auth.tokenStorageKey);
  },

  /**
   * Update the current user's profile.
   * @param {Object} profileData - Profile fields to update.
   * @returns {Promise<Object>} The updated user data.
   */
  updateProfile: async (profileData) => {
    try {
      const updatedUser = await authApi.updateProfile(profileData);
      localStorage.setItem(appConfig.auth.userStorageKey, JSON.stringify(updatedUser));
      return updatedUser;
    } catch (error) {
      console.error("Update profile error:", error);
      throw error;
    }
  }
};

export default authService;
